/**
 * Concepts tab — drafter-time audit trail of concept-group selections.
 *
 * For each binding the agent committed to: the group name (clickable to
 * the concept-group details dialog), the binding status (curated /
 * proposed / inline), the rationale prose explaining why this group
 * was chosen, and the alternatives the agent considered and rejected.
 *
 * The Description tab references these groups inline (auto-linked
 * concept-group ids in `qualifying_criteria` prose). This tab is the
 * audit surface — readers come here when they want to know *why* a
 * particular group was picked, not just which one. Splitting the two
 * keeps the description light prose for the reading flow and gives
 * provenance its own room without crowding the narrative.
 */

import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';

import { ConceptGroupDetailsDialog } from '@verantos-dev/feature-concept-groups-list';

import type { EpiArtifact, EpiConceptBinding } from '../../types/epi-chat-types';

const STATUS_COPY: Record<EpiConceptBinding['status'], { label: string; tooltip: string }> = {
  selected: {
    label: 'from library',
    tooltip: 'Selected an existing concept group from the library.',
  },
  proposed: {
    label: 'proposed',
    tooltip:
      'Built a new concept group from seeds + rules — pending curator review before this becomes a shared catalog group.',
  },
  inline: {
    label: 'inline',
    tooltip:
      'Small ad-hoc concept id list inlined into this analytical variable — no shared concept group exists for this binding.',
  },
};

const StatusBadge = ({ status }: { status: EpiConceptBinding['status'] }) => {
  const { label, tooltip } = STATUS_COPY[status];
  return (
    <Tooltip title={tooltip} placement="top">
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          fontSize: (theme) => theme.typography.caption.fontSize,
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: 'text.secondary',
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 0.75,
          paddingX: 0.75,
          paddingY: 0.25,
          marginLeft: 0.5,
        }}
      >
        {label}
      </Box>
    </Tooltip>
  );
};

const BindingRow = ({ binding }: { binding: EpiConceptBinding }) => {
  const [openDetails, setOpenDetails] = useState(false);
  const isInline = binding.status === 'inline';
  const alternatives = binding.alternativesConsidered ?? [];
  return (
    <Stack
      data-testid={`epi-concept-binding-${binding.conceptGroupId}`}
      spacing={0.5}
      sx={{
        paddingY: 1.25,
        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        '&:first-of-type': { borderTop: 0, paddingTop: 0 },
      }}
    >
      <Stack direction="row" alignItems="baseline" flexWrap="wrap" sx={{ gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {binding.conceptGroupName || binding.conceptGroupId}
        </Typography>
        <StatusBadge status={binding.status} />
        {/* Inline bindings have no shared concept group to drill into; skip
            the click affordance for them rather than render a dead link. */}
        {!isInline && (
          <Box
            component="button"
            type="button"
            onClick={() => setOpenDetails(true)}
            data-testid={`epi-concept-binding-open-${binding.conceptGroupId}`}
            sx={{
              border: 0,
              background: 'none',
              padding: 0,
              cursor: 'pointer',
              fontFamily: 'Menlo, Consolas, monospace',
              fontSize: (theme) => theme.typography.caption.fontSize,
              color: (theme) => theme.palette.primary.main,
              textDecoration: 'underline',
              textUnderlineOffset: 2,
              '&:hover': { color: (theme) => theme.palette.primary.dark },
              '&:focus-visible': {
                outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 1,
                borderRadius: 0.5,
              },
            }}
            aria-label={`Open concept group ${binding.conceptGroupId}`}
          >
            {binding.conceptGroupId}
          </Box>
        )}
      </Stack>
      {binding.rationale && (
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {binding.rationale}
        </Typography>
      )}
      {alternatives.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontStyle: 'italic', lineHeight: 1.5 }}
        >
          Considered: {alternatives.join(', ')}
        </Typography>
      )}
      {!isInline && openDetails && (
        <ConceptGroupDetailsDialog
          conceptGroupId={binding.conceptGroupId}
          onClose={() => setOpenDetails(false)}
        />
      )}
    </Stack>
  );
};

export const ConceptsTab = ({ artifact }: { artifact: EpiArtifact }) => {
  const bindings = artifact.conceptBindings ?? [];
  if (bindings.length === 0) {
    return (
      <Box sx={{ padding: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No concept bindings recorded yet. The agent will fill this once it commits to the concept
          groups this variable joins against.
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ padding: 2 }} data-testid="artifact-concepts-tab">
      <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1.25 }}>
        How this variable's clinical concepts were chosen. Each binding records the rationale and
        any alternatives the agent rejected — the audit trail for the concept selections the SQL
        joins against.
      </Typography>
      <Stack>
        {bindings.map((binding) => (
          <BindingRow key={binding.conceptGroupId} binding={binding} />
        ))}
      </Stack>
    </Box>
  );
};
